import { Controller } from "@hotwired/stimulus"

/**
 * PMI Measurements Controller
 * Manages chemical element columns configuration for PMI reports.
 *
 * Responsibilities:
 *   - Add/remove element columns (e.g., Cr, Ni, Mo)
 *   - Apply predefined material templates
 *   - Keep the hidden pmi_element_columns field in sync
 *   - Update element reading fields in existing measurement rows when columns change
 *
 * Usage in HAML:
 *   Attach to the PMI card wrapper with data-controller="pmi-measurements"
 */
export default class extends Controller {
  static targets = ["elementColumnsList", "elementInput", "hiddenElementColumns", "measurementRows"]

  // Predefined material templates
  static templates = {
    stainless_steel: ["Cr", "Ni", "Mo"],
    duplex: ["Cr", "Ni", "Mo", "N"],
    carbon_steel: ["C", "Mn", "Si"],
    nickel_alloy: ["Ni", "Cr", "Mo", "Fe", "Nb"],
    clear: []
  }

  connect() {
    // Initialize columns from the hidden field value
    this.columns = this.parseColumns()
  }

  /**
   * Parse current element columns from the hidden field
   * @returns {Array<string>} Array of element symbols
   */
  parseColumns() {
    if (!this.hasHiddenElementColumnsTarget) return []

    try {
      const value = this.hiddenElementColumnsTarget.value
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }

  /**
   * Add a new element column from the input field
   */
  addElement() {
    if (!this.hasElementInputTarget) return

    const element = this.elementInputTarget.value.trim()
    if (!element) return

    // Validate element name: must start with a letter, only alphanumeric
    if (!/^[A-Za-z][A-Za-z0-9]*$/.test(element)) return

    // Normalize: capitalize first letter
    const normalized = element.charAt(0).toUpperCase() + element.slice(1)

    // Prevent duplicates
    if (this.columns.includes(normalized)) {
      this.elementInputTarget.value = ""
      return
    }

    this.columns.push(normalized)
    this.elementInputTarget.value = ""

    this.updateUI()
    this.updateHiddenField()
    this.updateMeasurementRows()
  }

  /**
   * Remove an element column
   * @param {Event} event - Click event with element param
   */
  removeElement(event) {
    const element = event.params.element
    if (!element) return

    this.columns = this.columns.filter(col => col !== element)

    this.updateUI()
    this.updateHiddenField()
    this.updateMeasurementRows()
  }

  /**
   * Apply a predefined material template
   * @param {Event} event - Click event with template param
   */
  applyTemplate(event) {
    const templateName = event.params.template
    const template = this.constructor.templates[templateName]

    if (!template) return

    this.columns = [...template]

    this.updateUI()
    this.updateHiddenField()
    this.updateMeasurementRows()
  }

  /**
   * Update the element badges display
   */
  updateUI() {
    if (!this.hasElementColumnsListTarget) return

    // Remove all existing badges
    while (this.elementColumnsListTarget.firstChild) {
      this.elementColumnsListTarget.removeChild(this.elementColumnsListTarget.firstChild)
    }

    // Create badge for each element using DOM methods
    this.columns.forEach(element => {
      const badge = document.createElement("span")
      badge.className = "badge badge-info mr-2 mb-2 p-2"
      badge.dataset.element = element

      const text = document.createTextNode(element + " ")
      badge.appendChild(text)

      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "btn btn-sm ml-1 text-white"
      removeBtn.style.cssText = "background: none; border: none; padding: 0; font-size: 14px; line-height: 1;"
      removeBtn.dataset.action = "pmi-measurements#removeElement"
      removeBtn.dataset.pmiMeasurementsElementParam = element

      const icon = document.createElement("i")
      icon.className = "fa fa-times"
      removeBtn.appendChild(icon)

      badge.appendChild(removeBtn)
      this.elementColumnsListTarget.appendChild(badge)
    })
  }

  /**
   * Sync the hidden field with current columns
   */
  updateHiddenField() {
    if (!this.hasHiddenElementColumnsTarget) return

    this.hiddenElementColumnsTarget.value = JSON.stringify(this.columns)
  }

  /**
   * Update element reading fields in all existing measurement rows
   * Adds fields for new elements, removes fields for removed elements
   */
  updateMeasurementRows() {
    if (!this.hasMeasurementRowsTarget) return

    const rows = this.measurementRowsTarget.querySelectorAll(".nested-fields")

    rows.forEach(row => {
      let readingsRow = row.querySelector(".element-readings-row")

      if (this.columns.length === 0) {
        // Remove the readings row if no elements
        if (readingsRow) readingsRow.remove()
        return
      }

      if (!readingsRow) {
        // Create the readings row container
        readingsRow = document.createElement("div")
        readingsRow.className = "row mt-2 element-readings-row"

        // Insert before the comments row (last .row in the nested-fields)
        const allRows = row.querySelectorAll(":scope > .row")
        const lastRow = allRows[allRows.length - 1]
        if (lastRow) {
          row.insertBefore(readingsRow, lastRow)
        } else {
          row.appendChild(readingsRow)
        }
      }

      // Determine the field name index from an existing input in this row
      const existingInput = row.querySelector("input[name*='pmi_measurements_attributes']")
      let fieldIndex = "0"
      if (existingInput) {
        const match = existingInput.name.match(/pmi_measurements_attributes\]\[(\d+)\]/)
        if (match) fieldIndex = match[1]
      }

      // Get current element fields
      const currentFields = readingsRow.querySelectorAll(".element-reading-field")
      const currentElements = new Set()
      currentFields.forEach(field => {
        currentElements.add(field.dataset.element)
      })

      // Track which elements we want
      const wantedElements = new Set(this.columns)

      // Remove fields for elements no longer in columns
      currentFields.forEach(field => {
        if (!wantedElements.has(field.dataset.element)) {
          field.closest(".col-md-2").remove()
        }
      })

      // Add fields for new elements
      this.columns.forEach(element => {
        if (currentElements.has(element)) return

        const col = document.createElement("div")
        col.className = "col-md-2 mb-2"

        const label = document.createElement("label")
        label.className = "font-weight-bold"
        label.textContent = element

        const input = document.createElement("input")
        input.type = "text"
        input.className = "form-control form-control-sm element-reading-field"
        input.placeholder = "%"
        input.dataset.element = element
        input.name = `report[pmi_measurements_attributes][${fieldIndex}][element_readings][${element}]`

        col.appendChild(label)
        col.appendChild(input)
        readingsRow.appendChild(col)
      })

      // Reorder fields to match column order
      this.columns.forEach(element => {
        const field = readingsRow.querySelector(`.element-reading-field[data-element="${element}"]`)
        if (field) {
          readingsRow.appendChild(field.closest(".col-md-2"))
        }
      })
    })
  }
}
